import { $$ } from '../utils/dom.js';
import { gsap, ScrollTrigger } from '../vendor/vendor-globals.js';

const SELECTOR = '.reveal';
const MEDIA_SELECTOR = '.reveal--media';
const IMAGE_SELECTOR = 'img, picture, video';
const START = 'top 88%';
const COPY_DURATION = 0.85;
const MEDIA_DURATION = 1.15;
const STAGGER = 0.12;
const EASE = 'power3.out';

export function initReveal() {
  const root = document.documentElement;
  const elements = $$(SELECTOR);

  if (!elements.length || !gsap || !ScrollTrigger) {
    return () => {};
  }

  gsap.registerPlugin(ScrollTrigger);
  root.classList.add('js-reveal');

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach((el) => el.classList.add('is-revealed'));
    return () => {};
  }

  const { wipe, fade, copy } = splitTargets(elements);
  const shift = readCopyShift();

  if (copy.length) {
    gsap.set(copy, { autoAlpha: 0, y: shift });
  }

  if (fade.length) {
    gsap.set(fade, { autoAlpha: 0 });
  }

  if (wipe.length) {
    gsap.set(wipe, {
      autoAlpha: 0,
      clipPath: 'inset(100% 0% 0% 0%)',
    });
  }

  let triggers = [];
  let observer = null;

  const start = () => {
    triggers = ScrollTrigger.batch(SELECTOR, {
      start: START,
      once: true,
      onEnter: (batch) => animateBatch(batch),
    });
    ScrollTrigger.refresh();
  };

  if (root.getAttribute('data-loader') === 'active') {
    observer = new MutationObserver(() => {
      if (root.getAttribute('data-loader') !== 'active') {
        observer.disconnect();
        observer = null;
        start();
      }
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-loader'] });
  } else {
    start();
  }

  const onLoad = () => {
    if (root.getAttribute('data-loader') !== 'active') {
      ScrollTrigger.refresh();
    }
  };

  window.addEventListener('load', onLoad);

  return () => {
    observer?.disconnect();
    window.removeEventListener('load', onLoad);
    triggers.forEach((trigger) => trigger.kill());
  };
}

function animateBatch(elements) {
  const { wipe, fade, copy } = splitTargets(elements);

  if (copy.length) {
    gsap.to(copy, {
      autoAlpha: 1,
      y: 0,
      duration: COPY_DURATION,
      stagger: STAGGER,
      ease: EASE,
      overwrite: true,
      onStart() {
        markRevealed(copy);
      },
    });
  }

  if (fade.length) {
    gsap.to(fade, {
      autoAlpha: 1,
      duration: COPY_DURATION,
      stagger: STAGGER,
      ease: EASE,
      overwrite: true,
      onStart() {
        markRevealed(fade);
      },
    });
  }

  if (wipe.length) {
    gsap.to(wipe, {
      autoAlpha: 1,
      clipPath: 'inset(0% 0% 0% 0%)',
      duration: MEDIA_DURATION,
      stagger: STAGGER,
      ease: EASE,
      overwrite: true,
      onStart() {
        markRevealed(wipe);
      },
      onComplete() {
        gsap.set(wipe, { clearProps: 'clipPath' });
      },
    });
  }
}

function splitTargets(elements) {
  const wipe = elements.filter((el) => el.matches(MEDIA_SELECTOR));
  const fade = elements.filter(
    (el) => !el.matches(MEDIA_SELECTOR) && el.matches(IMAGE_SELECTOR),
  );
  const copy = elements.filter(
    (el) => !el.matches(MEDIA_SELECTOR) && !el.matches(IMAGE_SELECTOR),
  );

  return { wipe, fade, copy };
}

function markRevealed(elements) {
  elements.forEach((el) => el.classList.add('is-revealed'));
}

function readCopyShift() {
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue('--spacing-5')
      .trim() || '20px'
  );
}
